import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $dfs } from "@lexical/utils";
import { $createParagraphNode, $getRoot } from "lexical";
import { useEffect } from "react";

import type { TBeneficiariesFormData } from "..";
import type { SignatureBlockNode } from "../nodes/SignatureBlockNode";
import { $createSignatureBlockNode, $isSignatureBlockNode } from "../nodes/SignatureBlockNode";

/**
 * Render signature block plugin
 */
interface ISignatureBlockPlugin {
    /**
     * Name of the company
     */
    companyName: string;
    /**
     * If the signature block is called from agreement settings
     */
    isAgreementSettings: boolean;
    /**
     * Selected beneficiaries for multi-signature data
     */
    beneficiariesFormData: TBeneficiariesFormData | undefined;
    /**
     * If the signature block is called from agreement page (validation)
     */
    isAgreementEdit: boolean;
    /**
     * If the signature block is called from create agreement modal
     */
    isAgreementSetup: boolean;
    templateId?: number;
    /**
     * Whether the company has an active Docoon integration
     */
    hasDocoonIntegration?: boolean;
}

export const SignatureBlockPlugin = ({
    companyName,
    isAgreementSettings,
    beneficiariesFormData,
    isAgreementEdit,
    isAgreementSetup,
    templateId,
    hasDocoonIntegration,
}: ISignatureBlockPlugin) => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        editor.update(() => {
            const allNodes = $dfs();
            const signatureBlockNode = allNodes.find(({ node }) => $isSignatureBlockNode(node))
                ?.node as SignatureBlockNode;

            // When Docoon is active, remove any existing signature blocks and don't create new ones
            if (hasDocoonIntegration && (isAgreementSettings || isAgreementSetup)) {
                if (signatureBlockNode) {
                    signatureBlockNode.remove();
                }
                return;
            }

            /**
             * Only create signatory block if editor doesn't have one
             */
            if (!signatureBlockNode && isAgreementSettings) {
                const root = $getRoot();
                const signatureBlockNode = $createSignatureBlockNode(
                    companyName,
                    undefined,
                    isAgreementSettings,
                    isAgreementEdit,
                    templateId,
                );
                root.append($createParagraphNode());
                root.append($createParagraphNode());
                root.append(signatureBlockNode);
                root.append($createParagraphNode());
                root.append($createParagraphNode());
                root.append($createParagraphNode());
            }
            /**
             * Format signatory block for create agreement modal
             * Logic : Replace the existant SignatureBlock node with the right formatted one
             * With a signatory box for each beneficiary
             */
            if (isAgreementSetup || signatureBlockNode) {
                const root = $getRoot();
                const newSignatureBlockNode = $createSignatureBlockNode(
                    companyName,
                    beneficiariesFormData,
                    isAgreementSettings,
                    isAgreementEdit,
                    templateId,
                );
                if (signatureBlockNode) {
                    signatureBlockNode.replace(newSignatureBlockNode);
                } else {
                    root.append($createParagraphNode());
                    root.append($createParagraphNode());
                    root.append(newSignatureBlockNode);
                }
            }
        });
    }, []);
    return null;
};
